<template>
  <div class="ai-chat-layout bg-white rounded-lg shadow-sm border border-neutral-200">
    
    <!-- Backdrop for mobile sidebar -->
    <div v-if="showMobileSidebar" class="mobile-backdrop absolute inset-0 bg-black bg-opacity-50 z-40" @click="showMobileSidebar = false"></div>

    <div class="ai-sidebar bg-white border-r border-neutral-200" :class="{ 'mobile-open': showMobileSidebar }">
      <div class="ai-sidebar-header p-4 border-b border-neutral-200 flex justify-between items-center">
        <button 
          @click="startNewChat"
          class="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors duration-200"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd" />
          </svg>
          New Chat
        </button>
        <button v-if="showMobileSidebar" @click="showMobileSidebar = false" class="ml-2 p-2 text-neutral-500 bg-neutral-100 rounded-lg mobile-close-btn">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div class="ai-sidebar-list p-3 custom-scrollbar">
          <p class="text-xs font-semibold text-neutral-400 uppercase tracking-wider px-2 mb-2">Riwayat</p>
          
          <div v-if="isSidebarLoading" class="p-4 text-center text-sm text-neutral-400">
             Loading history...
          </div>

          <div v-else>
            <div 
                v-for="(history, index) in sessions" 
                :key="history.id || index"
                @click="selectHistory(history.id)"
                class="group p-3 mb-2 rounded-xl cursor-pointer transition-all duration-200 border"
                :class="activeChatId === history.id 
                ? 'bg-blue-50 border-blue-200 shadow-sm' 
                : 'bg-white border-transparent hover:bg-neutral-100'"
            >
                <h4 class="text-sm font-semibold text-neutral-800 mb-1 truncate">
                {{ history.title }}
                </h4>

                <p class="text-xs text-neutral-500 truncate mb-1.5 opacity-90">
                {{ history.lastMessage || 'Klik untuk melihat pesan...' }}
                </p>

                <div class="flex justify-end border-t border-dashed border-neutral-200 pt-1.5 mt-1">
                    <span class="text-xs font-medium text-neutral-400 group-hover:text-neutral-500 transition-colors">
                    {{ history.date }}
                    </span>
                </div>
            </div>
        </div>
      </div>
    </div>

    <div class="ai-chat-room bg-white relative">
      
      <!-- HEADER MOBILE ONLY -->
      <div class="mobile-header justify-between items-center p-3 border-b border-neutral-200 bg-white">
        <div class="flex items-center gap-2">
           <button @click="showMobileSidebar = true" class="p-2 bg-neutral-100 rounded-md text-neutral-600 hover:bg-neutral-200">
             <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
               <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
             </svg>
           </button>
           <h3 class="font-semibold text-neutral-700">AI Assistant</h3>
        </div>
      </div>

      <div class="ai-chat-messages bg-neutral-50 p-6 custom-scrollbar" ref="chatContainer">
        
        <div v-if="currentMessages.length === 0 && !isLoading" class="h-full flex flex-col items-center justify-center text-center opacity-60">
            <div class="w-16 h-16 bg-neutral-200 rounded-full flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
            </div>
            <h3 class="text-lg font-medium text-neutral-700">Mulai percakapan baru</h3>
            <p class="text-sm text-neutral-500">Tanyakan tentang pengiriman, alamat, atau minta buatkan pengiriman.</p>
        </div>

        <div 
          v-for="(msg, index) in currentMessages" 
          :key="index" 
          class="flex w-full mb-6 last:mb-0"
          :class="msg.role === 'user' ? 'justify-end' : 'justify-start'"
        >
            <div v-if="msg.role === 'agent'" class="w-8 h-8 rounded-full bg-blue-100 flex-shrink-0 flex items-center justify-center mr-2 mt-1 text-blue-600 text-xs font-bold">AI</div>

            <div class="max-w-[85%] sm:max-w-[75%]"> 
                <div 
                  class="px-4 py-3 rounded-2xl text-sm leading-relaxed shadow-sm markdown-content"
                  :class="[
                    msg.role === 'user' 
                        ? 'bg-blue-600 text-white rounded-br-none' 
                        : 'bg-white text-neutral-800 border border-neutral-200 rounded-bl-none'
                  ]"
                  v-html="renderMarkdown(msg.content)"
                >
                </div>
            </div>
        </div>

        <div v-if="isLoading" class="flex w-full mb-6 justify-start">
           <div class="w-8 h-8 rounded-full bg-blue-100 flex-shrink-0 flex items-center justify-center mr-2 mt-1 text-blue-600 text-xs font-bold">AI</div>
           <div class="bg-white border border-neutral-200 px-4 py-3 rounded-2xl rounded-bl-none shadow-sm flex items-center gap-1">
              <span class="w-1.5 h-1.5 bg-neutral-400 rounded-full animate-bounce"></span>
              <span class="w-1.5 h-1.5 bg-neutral-400 rounded-full animate-bounce" style="animation-delay: 0.1s"></span>
              <span class="w-1.5 h-1.5 bg-neutral-400 rounded-full animate-bounce" style="animation-delay: 0.2s"></span>
           </div>
        </div>

      </div>

      <div class="ai-chat-input p-4 bg-white border-t border-neutral-200">
        <div class="relative flex items-end gap-2 bg-neutral-100 p-2 rounded-xl border border-neutral-200 focus-within:ring-2 focus-within:ring-blue-100 focus-within:border-blue-300 transition-all">
            <textarea 
                ref="msgInput"
                v-model="inputMessage"
                :disabled="isLoading"
                placeholder="Ketik pesan anda di sini..." 
                class="w-full bg-transparent border-none focus:ring-0 text-sm text-neutral-800 placeholder-neutral-400 resize-none py-2 px-2 max-h-32 focus:outline-none disabled:opacity-50"
                rows="1"
                style="min-height: 44px;"
            ></textarea>
            <button 
                @click="sendMessage"
                :disabled="!inputMessage.trim() || isLoading"
                class="p-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:bg-neutral-300 disabled:cursor-not-allowed transition-colors mb-1"
            >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 transform rotate-90" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                </svg>
            </button>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { marked } from 'marked'

export default {
  name: 'AiChatInterface',

  data() {
    return {
      showMobileSidebar: false,
      activeChatId: null,
      inputMessage: '',
      sessionId: null, // ID Sesi saat ini
      
      isLoading: false,       // Loading saat kirim pesan
      isSidebarLoading: false,// Loading saat ambil history
      
      currentMessages: [] // Array pesan yang tampil di layar
    }
  },

  computed: {
    ...mapGetters('aichat', ['sessions'])
  },

  async mounted() {
    // 1. Ambil History Sidebar saat load
    await this.fetchHistories()

    // 2. Cek apakah URL punya session_id, jika ya load pesannya
    const querySessionId = this.$route.query.session_id
    if (querySessionId) {
       this.selectHistory(querySessionId)
    }
  },

  methods: {
    ...mapActions('aichat', ['sendMessageAPI', 'getSessionHistoriesAPI', 'getMessagesAPI']),
    ...mapMutations('aichat', ['prependSession']),

    renderMarkdown(text) {
      if (!text) return '';
      return marked.parse(text); 
    },

    // --- Load Daftar Sesi (Sidebar) ---
    async fetchHistories() {
        this.isSidebarLoading = true
        try {
            await this.getSessionHistoriesAPI()
        } catch (error) {
            console.error("Gagal load history", error)
        } finally {
            this.isSidebarLoading = false
        }
    },

    // --- Start New Chat (Tombol Atas) ---
    startNewChat() {
      this.sessionId = null
      this.activeChatId = null
      this.currentMessages = []
      this.$router.replace({ query: {} }) // Hapus query params
      this.$nextTick(() => {
          if(this.$refs.msgInput) this.$refs.msgInput.focus();
      })
    },

    // --- Klik Session di Sidebar ---
    async selectHistory(id) {
      // Jangan reload jika klik ID yang sama
      if (this.sessionId === id) return

      this.activeChatId = id
      this.sessionId = id
      this.currentMessages = [] // Kosongkan dulu
      this.isLoading = true // Tampilkan loading di chat area
      this.showMobileSidebar = false // Tutup sidebar mobile setelah memilih histori

      // Update URL agar bisa di-share/refresh
      this.$router.replace({ query: { session_id: id } })

      try {
        const response = await this.getMessagesAPI(id)
        const messages = response.data.data.data // Asumsi format BE: { data: [{role, content}, ...] }
        
        // Mapping role BE (user/model) ke UI (user/agent)
        this.currentMessages = messages.map(m => ({
            role: m.role === 'model' ? 'agent' : 'user', 
            content: m.content
        }))
        
      } catch (error) {
        console.error("Gagal load pesan", error)
        this.currentMessages.push({
            role: 'agent',
            content: 'Gagal memuat riwayat pesan.'
        })
      } finally {
        this.isLoading = false
        this.scrollToBottom()
      }
    },

    // --- Kirim Pesan ---
    async sendMessage() {
      if (!this.inputMessage.trim() || this.isLoading) return

      const userText = this.inputMessage
      const isNewChat = !this.sessionId // Cek apakah ini chat baru

      // 1. Tampilkan pesan user secara optimis
      this.currentMessages.push({
        id: Date.now(),
        role: 'user',
        content: userText
      })

      this.inputMessage = ''
      this.scrollToBottom()
      this.isLoading = true

      try {
        const payload = {
          message: userText,
          session_id: this.sessionId // null jika new chat
        }

        const response = await this.sendMessageAPI({ payloads: payload })
        const data = response.data.data // { reply: "...", session_id: "..." }

        // 2. Jika ini chat baru, Backend mengembalikan session_id baru
        if (isNewChat && data.session_id) {
          this.sessionId = data.session_id
          this.activeChatId = this.sessionId
          
          // Update URL
          this.$router.replace({
            query: { session_id: this.sessionId }
          })

          // 3. Update Sidebar Secara Manual (Append to List)
          // Kita buat object session dummy agar user melihatnya langsung di list
          const newSessionItem = {
             id: data.session_id,
             title: userText.substring(0, 30) + (userText.length > 30 ? '...' : ''), // Title sementara
             lastMessage: "Klik untuk melihat isi pesan...",
             date: "Baru saja" // Tanggal sementara
          }
          this.prependSession(newSessionItem)
        }

        // 4. Masukkan balasan AI ke chat room
        this.currentMessages.push({
          id: Date.now() + 1,
          role: 'agent',
          content: data.reply
        })

      } catch (err) {
        console.error('AI Error:', err)
        this.currentMessages.push({
          id: Date.now() + 2,
          role: 'agent',
          content: 'Maaf, terjadi kesalahan pada server. Silakan coba lagi.'
        })
      } finally {
        this.isLoading = false
        this.scrollToBottom()
        this.$nextTick(() => {
            if(this.$refs.msgInput) this.$refs.msgInput.focus();
        })
      }
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.chatContainer
        if (el) el.scrollTop = el.scrollHeight
      })
    }
  }
}
</script>

<style lang="scss" scoped>
/* LAYOUT INTI */
.ai-chat-layout {
  display: flex; 
  width: 100%;
  height: calc(100vh - 200px); 
  overflow: hidden; 
  position: relative;
  @media screen and (max-width: 767px) {
    height: calc(100vh - 140px);
  }
}

.ai-sidebar {
  width: 330px; 
  min-width: 330px;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease;
  @media screen and (max-width: 767px) {
    position: absolute;
    z-index: 50;
    height: 100%;
    width: 280px;
    min-width: 280px;
    left: 0;
    top: 0;
    transform: translateX(-100%);
    border-right: 1px solid #e5e5e5;
    &.mobile-open {
      transform: translateX(0);
      box-shadow: 4px 0 15px rgba(0,0,0,0.1);
    }
  }
}

.mobile-backdrop {
  display: none;
  @media screen and (max-width: 767px) {
    display: block;
  }
}

.mobile-header {
  display: none;
  @media screen and (max-width: 767px) {
    display: flex;
  }
}

.mobile-close-btn {
  display: none;
  @media screen and (max-width: 767px) {
    display: block;
  }
}
.ai-sidebar-header { flex-shrink: 0; }
.ai-sidebar-list { flex: 1; overflow-y: auto; }

.ai-chat-room {
  flex: 1; 
  display: flex;
  flex-direction: column;
  min-width: 0; 
}
.ai-chat-messages { flex: 1; overflow-y: auto; }
.ai-chat-input { flex-shrink: 0; }

.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: #d1d5db transparent;
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background-color: #d1d5db; border-radius: 20px; }
}

/* =========================================
   STYLE UNTUK MARKDOWN (PENTING!)
   Ini agar list dan bold tampil benar 
   ========================================= */
::v-deep .markdown-content {
  /* Style untuk paragraf */
  p {
    margin-bottom: 0.5rem;
    &:last-child {
      margin-bottom: 0;
    }
  }

  /* Style untuk List Angka (1. 2. 3.) */
  ol {
    list-style-type: decimal;
    margin-left: 1.25rem; /* Memberi jarak kiri */
    margin-bottom: 0.5rem;
  }

  /* Style untuk List Bullet */
  ul {
    list-style-type: disc;
    margin-left: 1.25rem;
    margin-bottom: 0.5rem;
  }

  /* Style untuk item list */
  li {
    margin-bottom: 0.25rem;
  }

  /* Style untuk Bold (**text**) */
  strong, b {
    font-weight: 600; /* Semi-bold */
  }

  /* Style untuk Italic (*text*) */
  em, i {
    font-style: italic;
  }
}
</style>